import React, { useEffect, useRef, useState } from "react";
//import LegalNotice from "./LegalNotice";
import emailjs from "@emailjs/browser";

// ─────────────────────────────────────────────────────────────────────────────
import {
  Mail,
  Send,
  CheckCircle,
  Clock,
  Instagram,
  Youtube,
} from "lucide-react"; //Facebook
//import { FaXTwitter } from "react-icons/fa6";

import InstallAppButton from "./InstallAppButton";

// ─── EmailJS Config ── fill these 3 values and you're done ───────────────────
const EMAILJS_SERVICE_ID = "service_10136ob";
const EMAILJS_TEMPLATE_ID = "template_j7f6o4p";
const EMAILJS_PUBLIC_KEY = "Kb1GtAGuUiCibcJ9t";

// ─── Utility: intersection-observer reveal hook ───────────────────────────
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}
const InfoCard = ({ card }) => {
  const [hov, setHov] = useState(false);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "1rem",
        padding: "1.25rem 1.5rem",
        background: hov
          ? "linear-gradient(135deg,rgba(102,126,234,0.05),rgba(118,75,162,0.08))"
          : "#fff",
        border: hov ? "1.5px solid #c4b5fd" : "1.5px solid #ede9fe",
        borderRadius: "16px",
        transition: "all 0.25s",
        boxShadow: hov
          ? "0 8px 28px rgba(118,75,162,0.08)"
          : "0 2px 12px rgba(0,0,0,0.03)",
        transform: hov ? "translateY(-2px)" : "none",
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          flexShrink: 0,
          background:
            "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#7c3aed",
        }}
      >
        {card.icon}
      </div>

      <div>
        <div
          style={{
            fontSize: "0.72rem",
            fontWeight: "700",
            color: "#9ca3af",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            marginBottom: "0.15rem",
          }}
        >
          {card.label}
        </div>

        {card.href ? (
          <a
            href={card.href}
            style={{
              fontWeight: "700",
              fontSize: "0.95rem",
              color: "#1e1b4b",
              textDecoration: "none",
            }}
          >
            {card.value}
          </a>
        ) : (
          <div
            style={{
              fontWeight: "700",
              fontSize: "0.95rem",
              color: "#1e1b4b",
            }}
          >
            {card.value}
          </div>
        )}

        <div
          style={{
            fontSize: "0.8rem",
            color: "#9ca3af",
            marginTop: "0.1rem",
          }}
        >
          {card.sub}
        </div>
      </div>
    </div>
  );
};
// ─── Footer Link ──────────────────────────────────────────────────────────
const FooterLink = ({ href, onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={href || "#"}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        color: hover ? "#7c3aed" : "#4b5563",
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: hover ? "600" : "400",
        cursor: "pointer",
        transition: "color 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "0.4rem",
      }}
    >
      {hover && <span style={{ fontSize: "0.6rem", color: "#a78bfa" }}>▶</span>}
      {children}
    </a>
  );
};

// ─── Social Icon ──────────────────────────────────────────────────────────
const SocialIcon = ({ s }) => {
  const [hover, setHover] = useState(false);
  return (
    <a
      href={s.href}
      target="_blank"
      rel="noopener noreferrer"
      title={s.name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        border: hover ? `1.5px solid ${s.hoverBg}` : "1.5px solid #ede9fe",
        background: hover ? `${s.hoverBg}14` : "#fafafa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "1rem",
        textDecoration: "none",
        transition: "all 0.2s",
        transform: hover ? "translateY(-2px)" : "none",
      }}
    >
      {s.icon}
    </a>
  );
};

// ─── Inline Footer ────────────────────────────────────────────────────────
const Footer = ({ onNavigate }) => {
  const currentYear = new Date().getFullYear();
  const handleNavigation = (view) => {
    if (onNavigate) onNavigate(view);
    else window.location.href = view.startsWith("/") ? view : `/${view}`;
  };
  const socials = [
    {
      name: "Instagram",
      icon: <Instagram size={18} color="#E1306C" />,
      href: "https://www.instagram.com/noryq.ai",
      hoverBg: "#E1306C",
    },

    {
      name: "YouTube",
      icon: <Youtube size={18} color="#aa023aff" />,
      href: "https://youtube.com/@noryq-ai",
      hoverBg: "#FF0000",
    },
  ];

  const quickLinks = [
    { name: "Home", view: "home" },
    { name: "Trending", view: "trending" },
    { name: "Premium", view: "premium" },
    { name: "About Us", href: "/about" },
    { name: "Why Us", href: "/whyus" },
  ];
  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Cookie Policy", href: "/cookies" },
    { name: "Refund Policy", href: "/refund" },
    { name: "Grievance Officer", href: "/privacy#grievance" },
    { name: "Contact Us", href: "/contact" },
  ];
  return (
    <footer
      className="cu-footer"
      style={{
        background: "#fff",
        borderTop: "1px solid #ede9fe",
        padding: "4rem 2rem 2rem",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "3rem",
            marginBottom: "3rem",
          }}
        >
          <div>
            <a
              href="/"
              style={{
                textDecoration: "none",
                display: "inline-block",
                marginBottom: "1rem",
              }}
            >
              <span
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontWeight: "900",
                  fontSize: "1.5rem",
                  letterSpacing: "0.2em",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {/* Logo Section - Fixed Size */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    cursor: "pointer",
                    flexShrink: 0, // Prevents logo from shrinking
                  }}
                >
                  <img
                    src="/logo.png"
                    alt="NORYQ"
                    style={{
                      height: "50px",
                      width: "auto", // Maintains aspect ratio
                      display: "block",
                    }}
                  />
                </div>
              </span>
            </a>
            <p
              style={{
                color: "#6b7280",
                lineHeight: 1.7,
                fontSize: "0.875rem",
                marginBottom: 0,
              }}
            >
              Your AI-powered movie and TV show recommendation companion. Find
              your perfect watch based on your mood.
            </p>
            {/* Install CTA card */}
            <style>{`
              @media (max-width: 480px) {
                .noryq-install-card { padding: 1rem !important; }
                .noryq-install-card p { font-size: 0.88rem !important; }
                .noryq-install-label { display: none !important; }
              }
            `}</style>
            <div
              className="noryq-install-card"
              style={{
                background:
                  "linear-gradient(135deg, rgba(102,126,234,0.06) 0%, rgba(118,75,162,0.1) 100%)",
                border: "1.5px solid #ede9fe",
                borderRadius: "16px",
                padding: "1.1rem 1.25rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {/* Label row */}
              <div
                className="noryq-install-label"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              ></div>

              {/* Headline */}
              <p
                className="noryq-install-headline"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "0.95rem",
                  fontWeight: "700",
                  fontStyle: "italic",
                  color: "#1e1b4b",
                  lineHeight: 1.35,
                  margin: 0,
                }}
              >
                Take NORYQ everywhere.
                <br />
                <span
                  style={{
                    background: "linear-gradient(135deg,#667eea,#764ba2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Install it in one tap.
                </span>
              </p>

              {/* Button — full width, never overflows */}
              <div style={{ width: "100%", minWidth: 0 }}>
                <InstallAppButton />
              </div>
            </div>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Quick Links
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {quickLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink
                    href={link.href || "#"}
                    onClick={
                      link.view
                        ? (e) => {
                            e.preventDefault();
                            handleNavigation(link.view);
                          }
                        : undefined
                    }
                  >
                    {link.name}
                  </FooterLink>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Legal
              </h4>
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {legalLinks.map((link) => (
                <li key={link.name} style={{ marginBottom: "0.6rem" }}>
                  <FooterLink href={link.href}>{link.name}</FooterLink>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.6rem",
                marginBottom: "1.25rem",
              }}
            >
              <div
                style={{
                  width: "1.25rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <h4
                style={{
                  fontSize: "0.75rem",
                  fontWeight: "700",
                  color: "#7c3aed",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                Stay Updated
              </h4>
            </div>
            <p
              style={{
                color: "#6b7280",
                fontSize: "0.875rem",
                lineHeight: 1.7,
                marginBottom: "1rem",
              }}
            >
              Follow us for the latest recommendations and feature drops.
            </p>
            {/* Socials */}
            <div
              style={{ display: "flex", gap: "0.6rem", marginBottom: "1rem" }}
            >
              {socials.map((s) => (
                <SocialIcon key={s.name} s={s} />
              ))}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background:
                  "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.1))",
                border: "1px solid #c4b5fd",
                borderRadius: "999px",
                padding: "0.4rem 1rem",
                marginBottom: "1.25rem",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#667eea,#764ba2)",
                }}
              />
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#7c3aed",
                  fontWeight: 600,
                }}
              >
                v1.0.0 — Live
              </span>
            </div>
          </div>
        </div>
        <div
          style={{
            height: "1px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
            marginBottom: "1.5rem",
          }}
        />
        <div
          className="cu-footer-bottom"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "0.75rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
            © {currentYear} NORYQ. All rights reserved. Made with ❤️ in India
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              fontSize: "0.82rem",
              color: "#9ca3af",
              alignItems: "center",
            }}
          >
            <span>Powered by</span>
            <a
              href="https://vybex1.netlify.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <VybexLogo />
            </a>

            <span style={{ color: "#c4b5fd" }}>•</span>
            <span>v1.0.0</span>
          </div>
        </div>
        {/* <LegalNotice /> */}
      </div>
    </footer>
  );
};

// ─── Vybex Logo ──────────────────────────────────────────────────────────────
const VybexLogo = () => {
  const ref = useRef(null);
  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Michroma&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    const container = ref.current;
    if (!container) return;
    container.innerHTML = ""; // prevent double render in StrictMode
    "VYBEX".split("").forEach((ch, i) => {
      const s = document.createElement("span");
      Object.assign(s.style, {
        fontFamily: "'Michroma', sans-serif",
        background: "linear-gradient(135deg, #00d4ff, #7c3aed, #ec4899)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        opacity: "0",
        transform: "translateY(6px) scale(0.85)",
        display: "inline-block",
        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${i * 80}ms`,
      });
      s.textContent = ch;
      container.appendChild(s);
      setTimeout(
        () => {
          s.style.opacity = "1";
          s.style.transform = "translateY(0) scale(1)";
        },
        100 + i * 80,
      );
    });
  }, []);
  return (
    <span
      ref={ref}
      style={{
        display: "inline-flex",
        letterSpacing: "0.18em",
        fontSize: "0.9rem",
        background: "#0a0a18",
        padding: "4px 12px",
        borderRadius: "999px",
        border: "1px solid rgba(124,58,237,0.35)",
      }}
    />
  );
};
// ─── Styled Input ─────────────────────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div style={{ marginBottom: "1.5rem" }}>
    <label
      style={{
        display: "block",
        marginBottom: "0.5rem",
        fontSize: "0.8rem",
        fontWeight: "700",
        color: "#7c3aed",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
      }}
    >
      {label}
      {required && (
        <span style={{ color: "#a78bfa", marginLeft: "0.2rem" }}>*</span>
      )}
    </label>
    {children}
  </div>
);

const inputBase = {
  width: "100%",
  padding: "0.9rem 1.1rem",
  borderRadius: "12px",
  border: "1.5px solid #ede9fe",
  background: "#fafafa",
  color: "#1e1b4b",
  fontSize: "0.95rem",
  outline: "none",
  fontFamily: "'DM Sans', sans-serif",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

// ─── Main Component ───────────────────────────────────────────────────────
const ContactUs = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => {
    document.title = "Contact Us | NORYQ";
  }, []);
  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const [formRef, formVisible] = useReveal();
  const [infoRef, infoVisible] = useReveal();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [focusedField, setFocusedField] = useState(null);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Manual validation
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.subject.trim() ||
      !formData.message.trim()
    ) {
      setStatus("error");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setStatus("error");
      alert("Please enter a valid email address.");
      return;
    }

    setStatus("sending");

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          reply_to: formData.email,
          subject: formData.subject,
          message: formData.message,
          to_email: "noryq.official@gmail.com",
        },
        EMAILJS_PUBLIC_KEY,
      );

      setStatus("success");
      setFormData({ name: "", email: "", subject: "", message: "" });

      setTimeout(() => setStatus("idle"), 5000);
    } catch {
      setStatus("error");
      alert("Something went wrong. Try again.");
      setTimeout(() => setStatus("idle"), 4000);
    }
  };
  const getFieldStyle = (name) => ({
    ...inputBase,
    borderColor: focusedField === name ? "#a78bfa" : "#ede9fe",
    boxShadow:
      focusedField === name ? "0 0 0 3px rgba(167,139,250,0.15)" : "none",
    background: focusedField === name ? "#fff" : "#fafafa",
  });

  const infoCards = [
    {
      icon: <Mail size={22} />,
      label: "Email",
      value: "noryq.official@gmail.com",
      href: "mailto:noryq.official@gmail.com",
      sub: "Drop us a line anytime",
    },
    {
      icon: <Clock size={22} />,
      label: "Response Time",
      value: "24 – 48 hours",
      sub: "We get back to everyone",
    },
    {
      icon: <CheckCircle size={22} />,
      label: "Support Hours",
      value: "Mon – Fri",
      sub: "9 AM – 6 PM IST",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fafafa",
        color: "#1e1b4b",
        fontFamily: "'DM Sans', sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700;1,900&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #f5f3ff; }
        ::-webkit-scrollbar-thumb { background: #a78bfa; border-radius: 2px; }
        @keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes float-blob { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-18px) scale(1.04)} }
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #fff; color: #1e1b4b; }

        /* ── Responsive ────────────────────────────────────────── */

        /* Nav */
        @media (max-width: 480px) { .cu-nav { padding: 0 1.25rem !important; } }

        /* Hero */
        @media (max-width: 640px) { .cu-hero-inner { padding: 4.5rem 1.25rem 3.5rem !important; } }

        /* Main two-column layout: info + form → stack */
        .cu-main-grid { display: grid; grid-template-columns: 1fr 1.6fr; gap: 4rem; align-items: start; }
        @media (max-width: 860px) {
          .cu-main-grid { grid-template-columns: 1fr !important; gap: 2.5rem !important; }
        }

        /* Info cards: slide-in animation can break on mobile, just ensure full width */
        @media (max-width: 860px) {
          .cu-info-panel { transform: none !important; opacity: 1 !important; }
          .cu-form-panel  { transform: none !important; opacity: 1 !important; }
        }

        /* Name + Email side-by-side → stack on small screens */
        .cu-name-email-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 540px) {
          .cu-name-email-row { grid-template-columns: 1fr !important; }
        }

        /* Form card padding */
        @media (max-width: 480px) {
          .cu-form-card { padding: 1.5rem !important; }
        }

        /* Submit button full width always; success button full width on mobile */
        @media (max-width: 480px) {
          .cu-success-btn { width: 100% !important; }
          .cu-section { padding: 3rem 1.25rem !important; }
        }

        /* Footer */
        @media (max-width: 640px) {
          .cu-footer { padding: 3rem 1.25rem 1.5rem !important; }
          .cu-footer-bottom { flex-direction: column !important; align-items: flex-start !important; gap: 0.5rem !important; }
        }

        /* Install card */
        @media (max-width: 400px) {
          .noryq-install-label { display: none !important; }
          .noryq-install-card  { padding: 1rem !important; }
        }
      `}</style>

      {/* ── NAV ──────────────────────────────────────────────────────── */}
      <nav
        className="cu-nav"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(250,250,250,0.88)",
          backdropFilter: "blur(18px)",
          borderBottom: "1px solid #ede9fe",
          padding: "0 2.5rem",
          height: "64px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <a href="/" style={{ textDecoration: "none" }}>
          {/* Logo Section - Fixed Size */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              cursor: "pointer",
              flexShrink: 0, // Prevents logo from shrinking
            }}
          >
            <img
              src="/logo.png"
              alt="NORYQ"
              style={{
                height: "50px",
                width: "auto", // Maintains aspect ratio
                display: "block",
              }}
            />
          </div>
        </a>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#fff",
          borderBottom: "1px solid #ede9fe",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-15%",
            right: "-8%",
            width: "50vw",
            height: "50vw",
            maxWidth: "620px",
            maxHeight: "620px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(102,126,234,0.09) 0%, rgba(118,75,162,0.05) 50%, transparent 70%)",
            animation: "float-blob 8s ease-in-out infinite",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "-10%",
            left: "-6%",
            width: "36vw",
            height: "36vw",
            maxWidth: "460px",
            maxHeight: "460px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(118,75,162,0.06) 0%, transparent 70%)",
            animation: "float-blob 11s ease-in-out infinite reverse",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(circle, #d8d0f8 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            opacity: 0.35,
          }}
        />

        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            padding: "7rem 2rem 6rem",
            position: "relative",
            zIndex: 1,
            textAlign: "center",
          }}
        >
          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              background:
                "linear-gradient(135deg,rgba(102,126,234,0.07),rgba(118,75,162,0.12))",
              border: "1px solid #c4b5fd",
              borderRadius: "999px",
              padding: "0.4rem 1.1rem",
              marginBottom: "2rem",
              transform: heroVisible ? "translateY(0)" : "translateY(14px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.6s ease 0.1s, opacity 0.6s ease 0.1s",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                animation: "pulse-dot 1.8s infinite",
              }}
            />
            <span
              style={{
                fontSize: "0.74rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: "#7c3aed",
                fontWeight: 700,
              }}
            >
              We're Here to Help
            </span>
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(2.6rem, 6vw, 5rem)",
              fontWeight: "900",
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              color: "#1e1b4b",
              marginBottom: "1.5rem",
              transform: heroVisible ? "translateY(0)" : "translateY(22px)",
              opacity: heroVisible ? 1 : 0,
              transition:
                "transform 0.75s cubic-bezier(.22,1,.36,1) 0.2s, opacity 0.75s ease 0.2s",
            }}
          >
            Get in
            <br />
            <span
              style={{
                fontStyle: "italic",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Touch.
            </span>
          </h1>

          <p
            style={{
              fontSize: "clamp(0.95rem, 1.8vw, 1.1rem)",
              lineHeight: 1.8,
              color: "#6b7280",
              maxWidth: "480px",
              margin: "0 auto",
              transform: heroVisible ? "translateY(0)" : "translateY(18px)",
              opacity: heroVisible ? 1 : 0,
              transition: "transform 0.7s ease 0.35s, opacity 0.7s ease 0.35s",
            }}
          >
            Have a question, suggestion, or just want to say hi? Send us a
            message — we read and respond to every one.
          </p>
        </div>

        <div
          style={{
            height: "2px",
            background:
              "linear-gradient(90deg, transparent, #c4b5fd 40%, #a78bfa 60%, transparent)",
          }}
        />
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────────────── */}
      <div
        className="cu-section"
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          padding: "5rem 2rem 4rem",
        }}
      >
        <div className="cu-main-grid" style={{}}>
          {/* ── LEFT: Info cards ──────────────────────────────────────── */}
          <div
            ref={infoRef}
            style={{
              transform: infoVisible ? "translateX(0)" : "translateX(-28px)",
              opacity: infoVisible ? 1 : 0,
              transition:
                "transform 0.8s cubic-bezier(.22,1,.36,1), opacity 0.8s ease",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  width: "2rem",
                  height: "2px",
                  background: "linear-gradient(90deg,#667eea,#764ba2)",
                  borderRadius: "2px",
                }}
              />
              <span
                style={{
                  fontSize: "0.73rem",
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: "#7c3aed",
                  fontWeight: 700,
                }}
              >
                Support
              </span>
            </div>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: "clamp(1.8rem, 3vw, 2.6rem)",
                fontWeight: "900",
                lineHeight: 1.15,
                letterSpacing: "-0.03em",
                color: "#1e1b4b",
                marginBottom: "0.75rem",
              }}
            >
              We respond
              <br />
              to everyone.
            </h2>
            <p
              style={{
                color: "#6b7280",
                lineHeight: 1.75,
                fontSize: "0.93rem",
                marginBottom: "2.5rem",
              }}
            >
              No bots, no automated replies. A real human from the NORYQ team
              will read your message and get back to you.
            </p>

            {/* Info cards */}
            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              {infoCards.map((card, i) => (
                <InfoCard key={i} card={card} />
              ))}
            </div>
          </div>

          {/* ── RIGHT: Form ───────────────────────────────────────────── */}
          <div
            ref={formRef}
            style={{
              transform: formVisible ? "translateX(0)" : "translateX(28px)",
              opacity: formVisible ? 1 : 0,
              transition:
                "transform 0.8s cubic-bezier(.22,1,.36,1) 0.1s, opacity 0.8s ease 0.1s",
            }}
          >
            <div
              style={{
                background: "#fff",
                border: "1.5px solid #ede9fe",
                borderRadius: "24px",
                padding: "2.5rem",
                boxShadow: "0 4px 40px rgba(102,126,234,0.07)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Glow */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "200px",
                  height: "200px",
                  background:
                    "radial-gradient(circle, rgba(118,75,162,0.06) 0%, transparent 70%)",
                  pointerEvents: "none",
                }}
              />

              {status === "success" ? (
                /* ── SUCCESS STATE ── */
                <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      margin: "0 auto 1.5rem",
                      background:
                        "linear-gradient(135deg,rgba(102,126,234,0.1),rgba(118,75,162,0.16))",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CheckCircle size={32} color="#7c3aed" />
                  </div>
                  <h2
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "1.8rem",
                      fontWeight: "900",
                      color: "#1e1b4b",
                      marginBottom: "0.75rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Message sent!
                  </h2>
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: 1.7,
                      marginBottom: "2rem",
                      fontSize: "0.95rem",
                    }}
                  >
                    Thank you for reaching out. We'll get back to you within
                    24–48 hours.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    style={{
                      background: "linear-gradient(135deg,#667eea,#764ba2)",
                      color: "#fff",
                      border: "none",
                      padding: "0.85rem 2rem",
                      borderRadius: "10px",
                      fontSize: "0.88rem",
                      fontWeight: "700",
                      cursor: "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                      letterSpacing: "0.04em",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.opacity = "0.85")
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                  >
                    Send Another →
                  </button>
                </div>
              ) : (
                /* ── FORM ── */
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: "2rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        marginBottom: "0.4rem",
                      }}
                    >
                      <div
                        style={{
                          width: "1.5rem",
                          height: "2px",
                          background: "linear-gradient(90deg,#667eea,#764ba2)",
                          borderRadius: "2px",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "0.73rem",
                          letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          color: "#7c3aed",
                          fontWeight: 700,
                        }}
                      >
                        Send a Message
                      </span>
                    </div>
                    <h3
                      style={{
                        fontFamily: "'Playfair Display', serif",
                        fontSize: "1.5rem",
                        fontWeight: "900",
                        color: "#1e1b4b",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      What's on your mind?
                    </h3>
                  </div>

                  {/* Name + Email row */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "1.25rem",
                    }}
                  >
                    <Field label="Your Name" required>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        placeholder="Jane Doe"
                        disabled={status === "sending"}
                        onChange={(e) =>
                          setFormData({ ...formData, name: e.target.value })
                        }
                        onFocus={() => setFocusedField("name")}
                        onBlur={() => setFocusedField(null)}
                        style={getFieldStyle("name")}
                      />
                    </Field>
                    <Field label="Email Address" required>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        placeholder="jane@example.com"
                        disabled={status === "sending"}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        onFocus={() => setFocusedField("email")}
                        onBlur={() => setFocusedField(null)}
                        style={getFieldStyle("email")}
                      />
                    </Field>
                  </div>

                  {/* Subject */}
                  <Field label="Topic" required>
                    <select
                      required
                      value={formData.subject}
                      disabled={status === "sending"}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      onFocus={() => setFocusedField("subject")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        ...getFieldStyle("subject"),
                        appearance: "none",
                        cursor: "pointer",
                      }}
                    >
                      <option value="">Select a topic</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Premium Support">Premium Support</option>
                      <option value="Technical Issue">Technical Issue</option>
                      <option value="Feature Request">Feature Request</option>
                      <option value="Billing Question">Billing Question</option>
                      <option value="Other">Other</option>
                    </select>
                  </Field>

                  {/* Message */}
                  <Field label="Your Message" required>
                    <textarea
                      required
                      value={formData.message}
                      rows={5}
                      placeholder="Tell us how we can help you..."
                      disabled={status === "sending"}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      onFocus={() => setFocusedField("message")}
                      onBlur={() => setFocusedField(null)}
                      style={{
                        ...getFieldStyle("message"),
                        resize: "vertical",
                        minHeight: "120px",
                      }}
                    />
                  </Field>

                  <p
                    style={{
                      fontSize: "0.78rem",
                      color: "#6b7280",
                      lineHeight: 1.6,
                      margin: "0 0 1rem",
                    }}
                  >
                    By sending this message you agree that NORYQ may use your
                    name, email and message to reply to you. We keep it for up
                    to 12 months. See our{" "}
                    <a href="/privacy" style={{ color: "#7c3aed" }}>
                      Privacy Policy
                    </a>
                    .
                  </p>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    style={{
                      width: "100%",
                      padding: "1rem",
                      background:
                        status === "sending"
                          ? "linear-gradient(135deg,#a78bfa,#c4b5fd)"
                          : "linear-gradient(135deg,#667eea,#764ba2)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "0.95rem",
                      fontWeight: "700",
                      letterSpacing: "0.06em",
                      cursor: status === "sending" ? "not-allowed" : "pointer",
                      fontFamily: "'DM Sans', sans-serif",
                      boxShadow:
                        status === "sending"
                          ? "none"
                          : "0 8px 28px rgba(102,126,234,0.28)",
                      transition: "transform 0.2s, box-shadow 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.6rem",
                    }}
                    onMouseEnter={(e) => {
                      if (status !== "sending") {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 16px 44px rgba(118,75,162,0.38)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 28px rgba(102,126,234,0.28)";
                    }}
                  >
                    {status === "sending" ? (
                      <>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            border: "2.5px solid rgba(255,255,255,0.35)",
                            borderTopColor: "#fff",
                            borderRadius: "50%",
                            animation: "spin 0.6s linear infinite",
                          }}
                        />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send size={18} /> Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactUs;
